import { ChevronsUpDown, X, Check } from "lucide-react";
import * as React from "react";

import { Button } from "@/common/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/common/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/common/components/ui/popover";
import { cn } from "@/common/lib/utils";

export interface SearchableComboboxOption {
  value: string;
  label: string;
}

interface SearchableComboboxProps {
  options: SearchableComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  onSearchChange?: (search: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
}

export function SearchableCombobox({
  options,
  value,
  onValueChange,
  onSearchChange,
  placeholder = "Выберите опцию...",
  searchPlaceholder = "Поиск...",
  emptyText = "Ничего не найдено.",
  className,
}: SearchableComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState<string>();

  React.useEffect(() => {
    if (typeof searchValue === "string") {
      onSearchChange?.(searchValue);
    }
  }, [searchValue]);

  const currentEmptyText =
    searchValue && searchValue.trim() !== "" ? emptyText : "Введите текст для поиска";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm leading-5 ring-offset-background text-Text-main font-normal truncate focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
              value ? "pr-8" : "",
              className,
            )}
          >
            {value ? (
              <span className="w-[95%] truncate text-left block overflow-hidden text-ellipsis whitespace-nowrap">
                {options.find((option) => option.value === value)?.label}
              </span>
            ) : (
              <span className="w-full h-full text-Buttons-btn-icon-btn_icon_disabled  hover:text-white inline-flex items-start">
                {placeholder}
              </span>
            )}{" "}
            {!value && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 shrink-0 opacity-50 hover:opacity-100 cursor-pointer flex items-center justify-center z-10 rounded"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onValueChange?.("");
              setSearchValue("");
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <PopoverContent className="w-80 md:w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>{currentEmptyText}</CommandEmpty>
            {searchValue && searchValue.trim() !== "" && (
              <CommandGroup>
                {options.map((option, index) => (
                  <CommandItem
                    key={`${option.value}-${index}`}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onValueChange?.(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
